import { useEffect, useState } from "react";
import { BookMarked, Save, Plus, Trash2 } from "lucide-react";
import { portalApi, apiError } from "@/lib/portalApi";
import { toast } from "sonner";

const input = "w-full bg-white border border-[#D8CEBE] rounded-md px-4 py-2.5 text-[#2A2825] font-body outline-none focus:border-[#A9724E]";
const label = "block text-xs tracking-overline uppercase text-[#8a7a6a] mb-1.5";
const WEEKS = Array.from({ length: 24 }, (_, i) => i + 1);
const TIERS = [
  { type: "rooted", name: "Rooted", hint: "The gentlest step · 10 drops" },
  { type: "growing", name: "Growing", hint: "A little more courage · 15 drops" },
  { type: "outpouring", name: "Outpouring", hint: "All the way · 20 drops" },
];

export default function ContentEditor() {
  const [all, setAll] = useState([]);
  const [week, setWeek] = useState(1);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => portalApi.get("/portal/content").then((r) => setAll(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const doc = all.find((c) => c.week === week);
    if (doc) setForm({ downloads: [], bonus: [], challenges: [], ...doc });
  }, [week, all]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setRow = (key, i, field, v) => setForm((f) => { const rows = [...(f[key] || [])]; rows[i] = { ...rows[i], [field]: v }; return { ...f, [key]: rows }; });
  const addRow = (key, blank) => setForm((f) => ({ ...f, [key]: [...(f[key] || []), blank] }));
  const delRow = (key, i) => setForm((f) => ({ ...f, [key]: f[key].filter((_, idx) => idx !== i) }));

  const challengeFor = (type) => (form.challenges || []).find((c) => c.type === type) || {};
  const setChallenge = (type, field, v) => setForm((f) => {
    const rows = [...(f.challenges || [])];
    const idx = rows.findIndex((c) => c.type === type);
    if (idx === -1) rows.push({ type, [field]: v });
    else rows[idx] = { ...rows[idx], [field]: v };
    return { ...f, challenges: rows };
  });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        scripture_ref: form.scripture_ref, scripture_text: form.scripture_text, prayer: form.prayer,
        reflection_prompt: form.reflection_prompt, practice_title: form.practice_title, practice_body: form.practice_body,
        hospitality: form.hospitality, teaching_video_url: form.teaching_video_url, teaching_audio_url: form.teaching_audio_url,
        downloads: (form.downloads || []).filter((d) => d.url), bonus: (form.bonus || []).filter((b) => b.title || b.body),
        bonus_unlocked: !!form.bonus_unlocked,
        challenges: (form.challenges || []).filter((c) => (c.title || "").trim() || (c.body || "").trim()),
      };
      const { data } = await portalApi.put(`/portal/content/${week}`, payload);
      setAll((a) => a.map((c) => (c.week === week ? data : c)));
      toast.success(`Week ${week} saved.`);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  if (!form) return null;

  return (
    <section data-testid="manage-content" className="bg-white/60 border border-[#E0D8C9] rounded-xl p-6 md:p-8 mt-6">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <BookMarked className="w-5 h-5 text-[#A9724E]" strokeWidth={1.5} />
          <h2 className="font-serif-display text-2xl">Weekly content</h2>
        </div>
        <div className="flex items-center gap-3">
          <select data-testid="content-week-select" className="bg-white border border-[#D8CEBE] rounded-md px-3 py-2 text-sm" value={week} onChange={(e) => setWeek(Number(e.target.value))}>
            {WEEKS.map((w) => {
              const doc = all.find((c) => c.week === w);
              const lbl = `Week ${w}${doc?.theme ? ` · ${doc.theme}` : ""}`;
              return <option key={w} value={w}>{lbl}</option>;
            })}
          </select>
          <button data-testid="content-save" onClick={save} disabled={saving} className="rounded-full bg-[#A9724E] hover:bg-[#925C3B] text-[#F4EFE6] px-5 py-2 text-sm inline-flex items-center gap-2 transition-colors disabled:opacity-50">
            <Save className="w-4 h-4" strokeWidth={1.5} />{saving ? "Saving…" : "Save week"}
          </button>
        </div>
      </div>

      <p className="text-sm text-[#8a7a6a] font-body mb-5">{form.movement} · Theme: {form.theme} · Practice: {form.practice}</p>

      <div className="grid md:grid-cols-2 gap-5">
        <div><label className={label}>Scripture reference</label><input data-testid="content-scripture-ref" className={input} value={form.scripture_ref || ""} onChange={(e) => set("scripture_ref", e.target.value)} /></div>
        <div><label className={label}>Practice title</label><input className={input} value={form.practice_title || ""} onChange={(e) => set("practice_title", e.target.value)} /></div>
        <div className="md:col-span-2"><label className={label}>Scripture text</label><textarea data-testid="content-scripture-text" rows={2} className={input} value={form.scripture_text || ""} onChange={(e) => set("scripture_text", e.target.value)} /></div>
        <div className="md:col-span-2"><label className={label}>Morning prayer</label><textarea data-testid="content-prayer" rows={2} className={input} value={form.prayer || ""} onChange={(e) => set("prayer", e.target.value)} /></div>
        <div className="md:col-span-2"><label className={label}>Reflection prompt</label><textarea rows={2} className={input} value={form.reflection_prompt || ""} onChange={(e) => set("reflection_prompt", e.target.value)} /></div>
        <div className="md:col-span-2"><label className={label}>Practice (daily)</label><textarea rows={2} className={input} value={form.practice_body || ""} onChange={(e) => set("practice_body", e.target.value)} /></div>
        <div className="md:col-span-2"><label className={label}>Hospitality</label><textarea rows={2} className={input} value={form.hospitality || ""} onChange={(e) => set("hospitality", e.target.value)} /></div>
        <div><label className={label}>Teaching video URL (YouTube/Vimeo/mp4)</label><input data-testid="content-video" className={input} value={form.teaching_video_url || ""} onChange={(e) => set("teaching_video_url", e.target.value)} /></div>
        <div><label className={label}>Teaching audio URL</label><input className={input} value={form.teaching_audio_url || ""} onChange={(e) => set("teaching_audio_url", e.target.value)} /></div>
      </div>

      {/* downloads */}
      <div className="mt-6">
        <div className="flex items-center justify-between mb-2"><label className={label}>Downloads / resources</label>
          <button onClick={() => addRow("downloads", { title: "", url: "" })} className="text-[#A9724E] text-xs inline-flex items-center gap-1"><Plus className="w-3.5 h-3.5" />Add</button></div>
        <div className="space-y-2">
          {(form.downloads || []).map((d, i) => (
            <div key={i} className="flex gap-2">
              <input placeholder="Title" className={input} value={d.title || ""} onChange={(e) => setRow("downloads", i, "title", e.target.value)} />
              <input placeholder="https://…" className={input} value={d.url || ""} onChange={(e) => setRow("downloads", i, "url", e.target.value)} />
              <button onClick={() => delRow("downloads", i)} className="text-[#b45c5c] shrink-0"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </div>

      {/* bonus */}
      <div className="mt-6 border-t border-[#E0D8C9] pt-5">
        <div className="flex items-center justify-between mb-3">
          <label className={`${label} mb-0`}>Bonus / surprise content</label>
          <label className="flex items-center gap-2 text-sm text-[#5E4B3C] cursor-pointer">
            <input data-testid="content-bonus-unlocked" type="checkbox" checked={!!form.bonus_unlocked} onChange={(e) => set("bonus_unlocked", e.target.checked)} />
            Unlock for cohort
          </label>
        </div>
        <button onClick={() => addRow("bonus", { title: "", body: "" })} className="text-[#A9724E] text-xs inline-flex items-center gap-1 mb-2"><Plus className="w-3.5 h-3.5" />Add bonus</button>
        <div className="space-y-3">
          {(form.bonus || []).map((b, i) => (
            <div key={i} className="flex gap-2">
              <div className="flex-1 space-y-2">
                <input placeholder="Title" className={input} value={b.title || ""} onChange={(e) => setRow("bonus", i, "title", e.target.value)} />
                <textarea placeholder="Body" rows={2} className={input} value={b.body || ""} onChange={(e) => setRow("bonus", i, "body", e.target.value)} />
              </div>
              <button onClick={() => delRow("bonus", i)} className="text-[#b45c5c] shrink-0"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </div>
      {/* daily challenges */}
      <div className="mt-6 border-t border-[#E0D8C9] pt-5" data-testid="content-challenges">
        <label className={`${label} mb-1`}>Daily challenges (Rooted · Growing · Outpouring)</label>
        <p className="text-xs text-[#8a7a6a] font-body mb-4">Write the three challenges members choose from this week. Leave a field blank to use the auto-written version from the theme.</p>
        <div className="space-y-4">
          {TIERS.map((tier) => {
            const c = challengeFor(tier.type);
            return (
              <div key={tier.type} className="bg-white/60 border border-[#E0D8C9] rounded-xl p-4">
                <p className="text-sm font-body text-[#2A2825] mb-0.5">{tier.name}</p>
                <p className="text-xs text-[#8a7a6a] font-body mb-3">{tier.hint}</p>
                <input data-testid={`challenge-${tier.type}-title`} placeholder="Challenge title" className={`${input} mb-2`}
                  value={c.title || ""} onChange={(e) => setChallenge(tier.type, "title", e.target.value)} />
                <textarea data-testid={`challenge-${tier.type}-body`} placeholder="What to actually do today…" rows={3} className={input}
                  value={c.body || ""} onChange={(e) => setChallenge(tier.type, "body", e.target.value)} />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
